import { getNoticeList } from "@/apis/user";
import { notice } from "@/components/Notification";
import { OrderStatus } from "@/constants";
import { useUserInfoStore } from "@/stores/userInfo";
import { List } from "@/types/Api/User/Notice/getNoticeList";
import { useNavigate } from "react-router-dom";

/**
 * 全局的通知与轮询，轮询将消息推入队列，并暴露消费者接口，每消费一个将消息从队列中删除。
 */
export class Notifications {
  private static _instance: Notifications;
  private _queue: unknown[] = [];
  private _timeout: number;
  /** 内部id，用于区分不同的轮询 */
  private _id: number = 0;
  /** 内部id, 对应timer的map */
  private _map: Record<number, NodeJS.Timeout> = {};
  private consumeFn?: <T>(msg: T) => Promise<void>;

  private constructor() {
    this._timeout = 5000;
    this._queue = [];
    this.loop();
  }

  static getInstance<A>(fn: (msg: A) => Promise<void>) {
    if (Notifications._instance) return Notifications._instance;
    Notifications._instance = new Notifications();
    Notifications._instance.consumeFn = fn as <A>(msg: A) => Promise<void>;
    return Notifications._instance;
  }

  polling(id: number, fn: () => Promise<unknown[]>) {
    const timer = setTimeout(async () => {
      this.polling(id, fn);
      // 开始请求api，并将消息推入队列
      try {
        const list = await fn();
        // 将消息推入队列
        this._queue.push(...list);
      } catch (error) {
        console.error(error);
      }
    }, this._timeout);
    this._map[id] = timer;
  }

  createPolling(fn: () => Promise<unknown[]>) {
    this._id++;
    this.polling(this._id, fn);
    return this._id;
  }

  stopPolling(id: number) {
    const timer = this._map[id];
    if (timer) {
      clearTimeout(timer);
      Reflect.deleteProperty(this._map, id);
    }
  }

  stopAllPolling() {
    Object.keys(this._map).forEach((id) => {
      this.stopPolling(Number(id));
    });
    this._map = {};
  }

  // 取出队列中的第一个消息进行消费
  consume() {
    if (this._queue.length === 0) {
      return null;
    }
    return this._queue.shift();
  }

  clear() {
    this.stopAllPolling();
    this._id = 0;
    this._queue = [];
  }

  protected loop() {
    setTimeout(() => {
      const msg = this.consume();
      if (msg) {
        // 消费消息
        this.consumeFn?.(msg);
        this.loop();
      } else {
        this.loop();
      }
    }, 16);
  }
}

export const NotificationTitles = {
  [OrderStatus.BUYER_REQUEST]: "买家请求交易",

  [OrderStatus.SELLER_CANCELED_DEAL]: "卖家取消交易",

  [OrderStatus.WAITING_FOR_TRANSFER]: "等待买家转账",
  [OrderStatus.BUYRER_CANCELD_DEAL]: "买家取消交易",
  [OrderStatus.TIME_OVER]: "超时取消订单",

  [OrderStatus.TRANSFERRED]: "等待卖家转币",
  [OrderStatus.SELLER_DID_NOT_RECEIVE]: "卖家未收到款",
  [OrderStatus.DISPUTING]: "争议订单",
  [OrderStatus.CANCELED_BY_OFFICIAL]: "平台取消交易",
  [OrderStatus.CONFIRMED_BY_OFFICIAL]: "平台确认交易",
  [OrderStatus.DEAL]: "卖家确认转币",
  [OrderStatus.BUYER_NOT_APPEALS]: "申诉期未申诉",
};

export const useNotification = (): [Notifications, () => void] => {
  const navigate = useNavigate();
  const [userInfo] = useUserInfoStore()((state) => [state.userInfo]);

  const notification = Notifications.getInstance<List>(async (msg) => {
    const title = NotificationTitles[msg.type as OrderStatus];

    // 提示音
    // @ts-ignore;
    const isH5Plus = !!window.plus;
    // @ts-ignore;
    if (isH5Plus) window.h5p.beep();

    await notice({
      title: title,
      content: msg.content,
      onClick: () => {
        const buyerIsMemberOrBusiness =
          msg.data.buyerType === "Member" || msg.data.buyerType === "Business";
        const sellerIsMemberOrBusiness =
          msg.data.buyerType === "Member" || msg.data.buyerType === "Business";

        if (userInfo.userId === msg.data.buyerId && buyerIsMemberOrBusiness) {
          const sellerAndBuyerBothAreBusiness =
            msg.data.sellerType === "Business" && userInfo.BusinessStatus === 1;
          navigate(
            `/buy_trading/${msg.data.orderId}?m=${
              sellerAndBuyerBothAreBusiness ? 1 : ""
            }`,
            {
              replace: window.location.pathname.indexOf("/buy_trading") > -1,
            }
          );
        }

        // to check
        if (userInfo.userId === msg.data.sellerId && sellerIsMemberOrBusiness) {
          // const sellerIsBusiness = userInfo.BusinessStatus === 1;
          const sellerIsBusiness = msg.data.sellerType === 'Business' && userInfo.BusinessStatus === 1;
          navigate(
            `/sell_trading/${msg.data.orderId}?m=${sellerIsBusiness ? 1 : ""}`,
            {
              replace: window.location.pathname.indexOf("/sell_trading") > -1,
            }
          );
        }
      },
    });
    return;
  });

  return [
    notification,
    () => {
      notification.createPolling(async () => {
        const { ok, data } = await getNoticeList({});
        if (ok) {
          return [...(data?.list || []), ...(data?.businessList || [])];
        } else {
          return [];
        }
        return Promise.resolve([]);
      });
    },
  ];
};
